import { SphericalMercator } from '@mapbox/sphericalmercator';

import {
    gsapTimelinePromise,
    isScreenAtLeast,
} from '../../../common/utils';

import els from '../elements';

import STATE_SETTINGS from './state-settings';

const TILE_SIZE = 256;

const COORD_ALIGNMENT = {
    height: 688.517,
    width: 1262,
    x: -1207,
    y: -2687,
    zoom: 4.940839999999996,
};

const INITIAL_MAP_ZOOM = {
    scale: 1.5,
    xPercent: -66,
    yPercent: -83,
};

const POPUP_FADE_DURATION = 0.1;
const POPUP_LINE_FADE_DURATION = 0.1;

const POPUP_MARGIN = {
    xPercent: 5,
    yPercent: 0,
};

let zooming = false;

let requestedInitialZoom = null;

const distanceBetween = (
    x1 = 0,
    y1 = 0,
    x2 = 0,
    y2 = 0
) => (
    Math.sqrt(
        Math.pow(
            x2 - x1,
            2
        )
        + Math.pow(
            y2 - y1,
            2
        )
    )
);

const degreesBetween = (
    x1 = 0,
    y1 = 0,
    x2 = 0,
    y2 = 0
) => (
    Math.atan2(
        y2 - y1,
        x2 - x1
    ) * (
        180 / Math.PI
    )
);

const coordsToStyles = (lat, lng) => {

    const mercator = new SphericalMercator({
        size: TILE_SIZE,
    });

    let [x, y] = (
        mercator.px(
            [lng, lat],
            COORD_ALIGNMENT.zoom
        )
    );

    x = x + COORD_ALIGNMENT.x;
    y = y + COORD_ALIGNMENT.y;

    const top = `${(y / COORD_ALIGNMENT.height) * 100}%`;
    const left = `${(x / COORD_ALIGNMENT.width) * 100}%`;

    return { top, left };
};

const setRequestedInitialZoom = (zoom) => {
    requestedInitialZoom = zoom;
};

const determineInitialZoom = () => ({
    ...INITIAL_MAP_ZOOM,
    ...(requestedInitialZoom ?? {}),
});

const setInitialPosition = () => {

    const $map = els.$map();

    gsap.set(
        $map,
        {
            width: $(window).width(),
            ...determineInitialZoom(),
        }
    );
};

const markStatesWithProperties = (cities) => {

    const $states = els.$states();

    const hasProperties = (state) => (
        cities.some((city) => (
            city.state.value === state.toUpperCase()
        ))
    );

    $states.each((_, element) => {

        const $state = $(element);
        const state = $state.attr('data-state');

        if (hasProperties(state)) {
            $state.attr('data-has-properties', '');
            $state.addClass('cursor-pointer');
        }
    });
};

const setCityPositions = (cities) => {

    cities.forEach((city) => {

        gsap.set(
            els.$city(city.id),
            {
                ...{
                    xPercent: -50,
                    yPercent: -50,
                },
                ...coordsToStyles(
                    city.latitude,
                    city.longitude
                ),
            }
        );
    });
};


const highlightState = (state) => {

    const $state = els.$state(state);
    const $count = els.$statePropertyCount(state);

    $state.addClass('fill-yellow');
    $count.find('path').addClass('fill-blue-dark');
};

const unhighlightState = (state) => {

    const $state = els.$state(state);
    const $count = els.$statePropertyCount(state);
    $state.removeClass('fill-yellow');
    $count.find('path').removeClass('fill-blue-dark');
};

const unhighlightStates = () => {

    const $states = els.$states();

    $states.each((_, element) => {
        const $state = $(element);
        const state = $state.attr('data-state');
        unhighlightState(state);
    });
};

const isZooming = () => zooming;

const zoomInOn = async (state) => {

    const $map = els.$map();
    const $statePropertyCounts = els.$statePropertyCounts();
    const $states = els.$states();
    const $stateDetails = els.$stateDetails();
    const $cityDropdownMenu = els.$cityDropdownMenu();
    const $cityLayer = els.$cityLayer();
    const $cities = els.$cities();

    const key = state.toUpperCase();

    if (
        !(key in STATE_SETTINGS)
        || !('zoomParameters' in STATE_SETTINGS[key])
    ) {
        console.error(`Missing zoom settings for "${key}".`);
        return;
    }

    const setting = STATE_SETTINGS[key];

    const zoomParameters = (
        isScreenAtLeast('md')
            ? setting.zoomParameters.desktop
            : setting.zoomParameters.mobile
    );

    const timeline = gsap.timeline();

    zooming = true;

    unhighlightStates();

    highlightState(state);

    timeline.set($map.find('path'), { strokeWidth: 1 });

    timeline.set($states, { cursor: 'default' });

    timeline.to($statePropertyCounts, { autoAlpha: 0 }, '<');

    if (isScreenAtLeast('md')) {
        timeline.to($stateDetails, { autoAlpha: 1 }, '<');
    } else {
        timeline.set(
            $cityDropdownMenu,
            { marginTop: -($cityDropdownMenu.outerHeight() + 1) },
            '<'
        );
        timeline.to($stateDetails, { marginTop: 0 }, '<');
    }

    timeline.to($cityLayer, { autoAlpha: 1 }, '<');

    timeline.to($cities, { scale: 1.0 / zoomParameters.scale }, '<');

    timeline.to($map, { ...zoomParameters }, '<');

    await gsapTimelinePromise(timeline);

    zooming = false;
};

const zoomOut = async () => {

    const $map = els.$map();
    const $statePropertyCounts = els.$statePropertyCounts();
    const $states = els.$states();
    const $stateDetails = els.$stateDetails();
    const $cityLayer = els.$cityLayer();

    const timeline = gsap.timeline();

    zooming = true;

    unhighlightStates();

    timeline.set($map.find('path'), { clearProps: 'strokeWidth' });

    timeline.set($states, { cursor: '' });

    timeline.to($statePropertyCounts, { autoAlpha: 1 }, '<');

    if (isScreenAtLeast('md')) {
        timeline.to($stateDetails, { autoAlpha: 0 }, '<');
    } else {
        timeline.to(
            $stateDetails,
            { marginTop: -$stateDetails.outerHeight() },
            '<'
        );
    }

    timeline.to($cityLayer, { autoAlpha: 0 }, '<');

    timeline.to($map, { ...determineInitialZoom() }, '<');

    await gsapTimelinePromise(timeline);

    zooming = false;
};

const prepareStateDetailsPopup = () => {

    const $stateDetails = els.$stateDetails();
    const $cityDropdownMenu = els.$cityDropdownMenu();

    gsap.set($cityDropdownMenu, { clearProps: 'marginTop' });

    if (isScreenAtLeast('md')) {
        gsap.set(
            $stateDetails,
            {
                autoAlpha: 0,
                clearProps: 'marginTop',
            }
        );
    } else {
        gsap.set(
            $stateDetails,
            {
                autoAlpha: 1,
                marginTop: -(
                    $stateDetails.height()
                    - $cityDropdownMenu.height()
                ),
            }
        );
    }
};

const reset = () => {

    const $statePropertyCounts = els.$statePropertyCounts();
    const $states = els.$states();
    const $cityLayer = els.$cityLayer();
    const $cityPopups = els.$cityPopups();
    const $cityPopupLines = els.$cityPopupLines();

    unhighlightStates();

    prepareStateDetailsPopup();

    gsap.set($cityPopups, { autoAlpha: 0 });

    resetCityPopupPositions();

    gsap.set($cityPopupLines, { autoAlpha: 0 });

    gsap.set($states, { cursor: '' });

    gsap.set($statePropertyCounts, { autoAlpha: 1 });

    gsap.set($cityLayer, { autoAlpha: 0 });

    setInitialPosition();
};

const getStatePropertyCountStyle = (state) => {

    const key = state.toUpperCase();

    if (
        !(key in STATE_SETTINGS)
        || !('labelPosition' in STATE_SETTINGS[key])
    ) {
        console.error(`Missing label position settings for "${key}".`);
        return { bottom: 0, left: 0 };
    }

    return STATE_SETTINGS[state]?.labelPosition;
};

const resetCityPopupPositions = () => {

    gsap.set(els.$cityPopups(), { clearProps: 'top,left' });
};

const positionCityPopup = (city) => {

    const $app = els.$app();
    const $map = els.$map();
    const $marker = els.$city(city.id);
    const $popup = els.$cityPopup(city.id);

    const isMarkerCloseToTop = (
        (
            $marker.offset().top
            - $app.offset().top
        )
        < ($app.outerHeight() * 0.3)
    );

    const centered = {
        top: (
            $marker.position().top
            + $map.position().top
        ),
        left: $marker.offset().left,
    };

    // Replaces the previous transform translate -50% x and y that was on the element, because Safari couldn't act right
    const translated = {
        top: (
            0 - ($popup.outerHeight() / 2)
        ),
        left: (
            0 - ($popup.outerWidth() / 2)
        ),
    }

    const offset = {
        top: (
            (isMarkerCloseToTop ? 0.7 : -0.5)
            * $popup.outerHeight()
        ),
        left: 0,
    };

    let top = (
        centered.top
        + translated.top
        + offset.top
        + $app.position().top
    );

    let left = (
        centered.left
        + translated.left
        + offset.left
    );

    gsap.set($popup, { top, left });

    const framingCorrection = (() => {

        let adjustment = {
            top: 0,
            left: 0,
        };

        const overflowingOnLeft = $popup.offset().left < 0;

        const overflowingOnRight = (
            $app.outerWidth()
            < (
                $popup.offset().left
                + $popup.outerWidth()
            )
        );

        const direction = overflowingOnRight ? 1 : -1;

        if (
            overflowingOnLeft
            || overflowingOnRight
        ) {

            const overflow = (
                $app.outerWidth()
                - (
                    $popup.offset().left
                    + $popup.outerWidth()
                )
            );

            const margin = (
                (POPUP_MARGIN.xPercent * 0.01)
                * $popup.outerWidth()
            );

            adjustment.left += (
                direction * (
                    overflow - margin
                )
            );
        }

        return adjustment;
    })();

    top += framingCorrection.top;
    left += framingCorrection.left;

    gsap.set($popup, { top, left });
};

const positionCityPopupLine = (city) => {

    const $line = els.$cityPopupLine(city.id);
    const $circle = els.$cityPopupCircle(city.id);
    const $marker = els.$city(city.id);

    const circlePosition = {
        top: (
            $circle.offset().top
            + ($circle.outerHeight() * 0.5)
        ),
        left: (
            $circle.offset().left
            + ($circle.outerWidth() * 0.5)
        ),
    };

    const cityPosition = {
        top: (
            $marker.offset().top
            + ($marker.outerHeight() * 0.5)
        ),
        left: (
            $marker.offset().left
            + ($marker.outerWidth() * 0.5)
        ),
    };

    const width = (
        distanceBetween(
            cityPosition.left,
            cityPosition.top,
            circlePosition.left,
            circlePosition.top
        )
    );

    const rotation = (
        degreesBetween(
            cityPosition.left,
            cityPosition.top,
            circlePosition.left,
            circlePosition.top
        )
    );

    gsap.set(
        $line,
        {
            transformOrigin: '0%',
            rotation,
            width,
        }
    )
};

const prepareCityPopupPropertyList = (city) => {

    const $circle = els.$cityPopupCircle(city.id);
    const $content = els.$cityPopupContent(city.id);
    const $list = els.$cityPopupPropertyList(city.id);
    const $items = els.$cityPopupPropertyListItems(city.id);
    const $bookends = els.$cityPopupPropertyListBookends(city.id);
    const hasMultiple = $items.length > 1;
    const useMaxListHeight = hasMultiple || !isScreenAtLeast('md');

    const itemHeight = $items.first().outerHeight();
    const maxListHeight = itemHeight * 3.5;

    gsap.set($content, { paddingBottom: 0 });

    if (useMaxListHeight) {
        gsap.set($list, { height: maxListHeight });
    }

    gsap.set(
        $bookends.first(),
        { height: itemHeight * (useMaxListHeight ? 1 : 0.3) }
    );

    gsap.set(
        $bookends.last(),
        { height: itemHeight * (useMaxListHeight ? 1.5 : 0.6) }
    );

    gsap.set($list, { scrollTop: 0 });

    const contentHeight = (
        useMaxListHeight
            ? $content.outerHeight()
            : (
                $content.outerHeight()
                - $list.outerHeight()
                + maxListHeight
            )
    );

    const circleHeight = (
        !isScreenAtLeast('md')
            ? contentHeight
            : contentHeight * 1.5
    );

    gsap.set($circle, { height: circleHeight });
};

const showCityPopup = async (city) => {

    const $city = els.$city(city.id);
    const $otherCities = els.$cities().not($city);
    const $popup = els.$cityPopup(city.id);
    const $line = els.$cityPopupLine(city.id);
    const $circle = els.$cityPopupCircle(city.id);
    const $content = els.$cityPopupContent(city.id);

    prepareCityPopupPropertyList(city);

    if (isScreenAtLeast('md')) {

        positionCityPopup(city);

        positionCityPopupLine(city);
    }

    const timeline = gsap.timeline();

    timeline.set($otherCities, { zIndex: 0 });
    timeline.set($city, { zIndex: 1 });

    timeline.to(
        $popup,
        {
            autoAlpha: 1,
            duration: 0,
        }
    );

    timeline.to(
        $line,
        {
            autoAlpha: 1,
            duration: 0,
        }
    );

    await gsapTimelinePromise(timeline);

    gsap.set(
        $circle.add($content),
        { pointerEvents: 'auto' }
    );
};

const hideCityPopup = async (city) => {

    const $popup = els.$cityPopup(city.id);
    const $line = els.$cityPopupLine(city.id);
    const $circle = els.$cityPopupCircle(city.id);
    const $content = els.$cityPopupContent(city.id);

    const timeline = gsap.timeline();

    timeline.set($content, { pointerEvents: 'none' });

    timeline.to(
        $line,
        {
            autoAlpha: 0,
            duration: 0,
        }
    );

    timeline.to(
        $popup,
        {
            autoAlpha: 0,
            duration: 0,
        }
    );

    await gsapTimelinePromise(timeline);

    gsap.set(
        $circle.add($content),
        { pointerEvents: 'none' }
    );
};

const openCityDropdown = async () => {

    const $menu = els.$cityDropdownMenu();

    const timeline = gsap.timeline();

    timeline.to($menu, { marginTop: 0 });

    await gsapTimelinePromise(timeline);
};

const closeCityDropdown = async () => {

    const $menu = els.$cityDropdownMenu();

    const timeline = gsap.timeline();

    timeline.to($menu, { marginTop: -$menu.outerHeight() });

    await gsapTimelinePromise(timeline);
};

const shiftPropertyListTo = async (property) => {

    const $list = els.$cityPopupPropertyList(property.city.id);
    const $item = (
        els.$cityPopupPropertyListItem(
            property.city.id,
            property.id
        )
    );

    await $item.scrollIntoView({
        scroller: $list.get(0),
        alignment: 'center',
    });
};

const initialize = ({
    cities,
    requestedInitialZoom = {},
}) => {

    setCityPositions(cities);

    setRequestedInitialZoom(requestedInitialZoom);

    markStatesWithProperties(cities);

    setInitialPosition();

    prepareStateDetailsPopup();
};

export default {
    setRequestedInitialZoom,
    highlightState,
    unhighlightState,
    unhighlightStates,
    zoomInOn,
    zoomOut,
    reset,
    getStatePropertyCountStyle,
    showCityPopup,
    hideCityPopup,
    openCityDropdown,
    closeCityDropdown,
    isZooming,
    initialize,
    prepareStateDetailsPopup,
    shiftPropertyListTo,
};

